/**
 * DailyBriefingModal — Modal proativo mostrado 1x/dia quando o admin entra.
 *
 * Só aparece quando:
 *  - Existem notificações abertas
 *  - Ainda não foi mostrado hoje (flag em localStorage: daily_briefing_<tenantId>_<YYYY-MM-DD>)
 *
 * Mostra até 5 sugestões priorizadas com botões de ação inline.
 * "Ver todas" navega para o Dashboard onde há a seção completa.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sparkles, ArrowRight, Check, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/contexts/NotificationsContext";

const PRIORITY_STYLE = {
  high: "bg-red-500/10 text-red-700 border-red-500/30",
  medium: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  low: "bg-zinc-500/10 text-zinc-700 border-zinc-500/30",
};
const PRIORITY_LABEL = { high: "Alta", medium: "Média", low: "Baixa" };

function todayKey(tenantId) {
  const d = new Date();
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return `daily_briefing_${tenantId}_${iso}`;
}

export default function DailyBriefingModal() {
  const nav = useNavigate();
  const { slug } = useParams();
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const { items, total, loaded, resolve, snooze } = useNotifications();

  useEffect(() => {
    if (!loaded || !auth?.tenant?.id) return;
    const key = todayKey(auth.tenant.id);
    if (localStorage.getItem(key)) return;
    if (total <= 0) return;
    // só admin/manager veem o pop-up
    const role = auth.user?.role;
    if (role !== "tenant_admin" && role !== "manager") return;

    setOpen(true);
    localStorage.setItem(key, "1");
  }, [loaded, total, auth?.tenant?.id, auth?.user?.role]);

  const top = items.slice(0, 5);

  const handleView = (url) => {
    if (!url) return;
    setOpen(false);
    nav(url);
  };

  const handleResolve = async (id) => {
    setBusy(id);
    try { await resolve(id); toast.success("Marcado como resolvido"); }
    catch { toast.error("Erro ao resolver"); }
    finally { setBusy(null); }
  };

  const handleSnooze = async (id) => {
    setBusy(id);
    try { await snooze(id, 7); toast.success("Adiado por 7 dias"); }
    catch { toast.error("Erro ao adiar"); }
    finally { setBusy(null); }
  };

  const firstName = auth?.user?.name?.split(" ")[0] || "";
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Bom dia";
    if (h < 18) return "Boa tarde";
    return "Boa noite";
  })();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="daily-briefing-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-tenant" />
            {greeting}{firstName ? `, ${firstName}` : ""}!
          </DialogTitle>
          <DialogDescription>
            Você tem <b>{total}</b> ponto(s) que requer(em) atenção. Aqui vão os mais importantes:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1" data-testid="daily-briefing-list">
          {top.map((n) => (
            <div
              key={n.id}
              className="rounded-lg border border-border p-3 space-y-2"
              data-testid={`briefing-item-${n.id}`}
            >
              <div className="flex items-start gap-2">
                <div className="text-sm font-medium leading-tight flex-1 min-w-0">{n.title}</div>
                <Badge variant="outline" className={`${PRIORITY_STYLE[n.priority]} text-[10px] shrink-0`}>
                  {PRIORITY_LABEL[n.priority] || n.priority}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground">{n.message}</div>
              <div className="flex items-center gap-1 flex-wrap">
                {(n.quick_actions || []).slice(0, 1).map((qa) => (
                  <Button
                    key={qa.key}
                    size="sm" variant="outline"
                    className="h-7 text-xs gap-1"
                    onClick={() => handleView(qa.url)}
                    data-testid={`briefing-action-view-${n.id}`}
                  >
                    {qa.label} <ArrowRight className="w-3 h-3" />
                  </Button>
                ))}
                <Button
                  size="sm" variant="ghost"
                  className="h-7 text-xs gap-1"
                  onClick={() => handleSnooze(n.id)}
                  disabled={busy === n.id}
                  data-testid={`briefing-snooze-${n.id}`}
                >
                  <Clock className="w-3 h-3" /> 7d
                </Button>
                <Button
                  size="sm" variant="ghost"
                  className="h-7 text-xs gap-1 text-emerald-700 hover:text-emerald-700 hover:bg-emerald-500/10"
                  onClick={() => handleResolve(n.id)}
                  disabled={busy === n.id}
                  data-testid={`briefing-resolve-${n.id}`}
                >
                  <Check className="w-3 h-3" /> Resolver
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <Button
            variant="ghost" size="sm"
            onClick={() => setOpen(false)}
            data-testid="briefing-dismiss-btn"
          >
            Fechar
          </Button>
          {slug && (
            <Button
              size="sm" variant="default"
              onClick={() => { setOpen(false); nav(`/t/${slug}/dashboard`); }}
              data-testid="briefing-view-all-btn"
            >
              Ver todas na Visão Geral <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
